import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { TEXT_GRAY } from '@/constants/colors';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { FeatureFlag } from '@/types/featureFlag';
import { useConfigValue } from '@/utils/useConfigValue';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { gql } from '@apollo/client';
import { FC } from 'react';

import { CareTeamClinicianFragmentFragment } from './PatientCareTeamInfo.gql.types';
import { sortRnsFirst, withCaseManagerRoles } from './PatientCareTeamInfo.utils';
import {
    HeadingCaptionLabel,
    LeftRailNoneLabel,
    LeftRailTextButton,
    LeftRailValueTypography,
} from './PatientLeftRail';
import { PatientLeftRailQuery } from './PatientLeftRail.gql.types';

export const CareTeamClinicianFragment = gql`
    fragment CareTeamClinicianFragment on Clinician {
        id
        firstName
        lastName
        fullName
        email
        disciplines {
            id
            displayName
        }
        licenses
    }
`;

export type ICareTeamClinician = CareTeamClinicianFragmentFragment;

type PatientCareTeamInfoPatient = Pick<
    NonNullable<PatientLeftRailQuery['patient']>,
    'id' | 'careTeams' | 'permissions'
>;

type CareTeamClinicianWithRole = ICareTeamClinician & {
    role?: string;
};

export const PatientCareTeamInfo = ({ patient }: { patient: PatientCareTeamInfoPatient }) => {
    const { openDialog } = useDialog();
    const applyRnCaseManagerLabel = useConfigValue('care_team_case_manager_label_for_rn');
    const hasRecommendationFlow = useFeatureFlagEnabled(
        FeatureFlag.DASHBOARD_CARE_TEAM_RECOMMENDATION_FLOW,
    );

    let careTeamClinicians: CareTeamClinicianWithRole[] = patient.careTeams.flatMap(
        (careTeam) => careTeam.clinicians,
    );
    if (applyRnCaseManagerLabel) {
        // Label RNs as Case Managers and float them to the top of the care team list.
        careTeamClinicians = sortRnsFirst(withCaseManagerRoles(careTeamClinicians));
    }

    return (
        <VerticalStack spacing={1}>
            <HorizontalStack justifyContent='space-between'>
                <HeadingCaptionLabel>Care Team</HeadingCaptionLabel>
                {patient.permissions.editCareTeam && (
                    <LeftRailTextButton
                        onClick={() =>
                            openDialog(
                                hasRecommendationFlow
                                    ? DialogType.EditPatientCareTeamRecommendation
                                    : DialogType.EditPatientCareTeam,
                                { patientId: patient.id },
                            )
                        }
                    >
                        Edit
                    </LeftRailTextButton>
                )}
            </HorizontalStack>
            <VerticalStack spacing={2}>
                {careTeamClinicians.length > 0 ? (
                    careTeamClinicians.map((clinician) => (
                        <CareTeamClinician key={clinician.id} clinician={clinician} />
                    ))
                ) : (
                    <LeftRailNoneLabel />
                )}
            </VerticalStack>
        </VerticalStack>
    );
};

interface ICareTeamClinicianProps {
    clinician: CareTeamClinicianWithRole;
    displayEmail?: boolean;
}

const CareTeamClinician: FC<ICareTeamClinicianProps> = ({ clinician }) => {
    const licenses = clinician.licenses?.length ? ` (${clinician.licenses.join(', ')})` : '';

    return (
        <VerticalStack spacing={0}>
            {clinician.role && <HeadingCaptionLabel>{clinician.role}</HeadingCaptionLabel>}

            <LeftRailValueTypography
                sx={{
                    color: TEXT_GRAY,
                    textDecoration: 'none',
                }}
            >
                {clinician.fullName + licenses}
            </LeftRailValueTypography>
        </VerticalStack>
    );
};
